// ═══════════════════════════════════════════════════════════════
// SeatManager.jsx — Seat management modal + alerts
// Extracted from SupportDashboard.jsx for billing module reuse.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { AlertCircle, X, Loader2, Users } from 'lucide-react';
import { PLAN_LIMITS } from './BillingConfig.js';

/**
 * Seat overage / near-capacity warnings.
 * <SeatAlerts tenant={tenant} activeSeats={n} onManageSeats={() => setShowPanel(true)} />
 */
function SeatAlerts({ tenant, activeSeats, onManageSeats }) {
  if (!tenant || tenant.maxSeats === 99999) return null;

  // Overage
  if (activeSeats >= tenant.maxSeats) {
    return (
      <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
        <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
        <p className="text-sm text-red-800 flex-1">
          Seat limit reached ({activeSeats}/{tenant.maxSeats}). New users cannot be added until seats are increased or inactive users are removed.
        </p>
        {onManageSeats && (
          <button
            onClick={onManageSeats}
            className="text-sm font-medium text-red-700 hover:text-red-900 underline flex-shrink-0"
          >
            Add seats
          </button>
        )}
      </div>
    );
  }

  // Near capacity (>= 90%)
  if (activeSeats >= tenant.maxSeats * 0.9) {
    return (
      <div className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0" />
        <p className="text-sm text-amber-800 flex-1">
          Approaching seat limit ({activeSeats}/{tenant.maxSeats}). Consider adding seats before the limit is reached.
        </p>
      </div>
    );
  }

  return null;
}

/**
 * "Manage seats" button trigger.
 * <SeatManagerButton tenant={tenant} activeSeats={n} onClick={() => setShowPanel(true)} />
 */
function SeatManagerButton({ tenant, activeSeats, onClick }) {
  if (tenant.maxSeats === 99999) return null;

  return (
    <div className="mb-4">
      <button
        onClick={onClick}
        className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
      >
        <Users className="w-4 h-4" />
        Manage seats ({activeSeats}/{tenant.maxSeats})
      </button>
    </div>
  );
}

/**
 * Seat management panel (modal-like inline panel).
 * <SeatManagementPanel
 *   tenant={tenant}
 *   activeSeats={n}
 *   stripeAdapter={adapter}
 *   onClose={() => {}}
 *   onUpdated={(newMaxSeats) => {}}
 * />
 */
function SeatManagementPanel({ tenant, activeSeats, stripeAdapter, onClose, onUpdated }) {
  const planLimits = PLAN_LIMITS[tenant.plan];
  const minSeats = planLimits ? planLimits.minSeats : 1;
  const maxSeats = planLimits ? planLimits.maxSeats : 999;
  const [newSeatCount, setNewSeatCount] = useState(tenant.maxSeats || minSeats);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleUpdate = async () => {
    setError('');
    if (newSeatCount < minSeats) {
      setError(`Minimum ${minSeats} seats for the ${tenant.plan} plan.`);
      return;
    }
    if (newSeatCount > maxSeats) {
      setError(`Maximum ${maxSeats} seats for the ${tenant.plan} plan.`);
      return;
    }
    if (newSeatCount < activeSeats) {
      setError(`Cannot reduce below ${activeSeats} seats because ${activeSeats} users are currently active. Deactivate users first.`);
      return;
    }
    if (newSeatCount === tenant.maxSeats) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const result = await stripeAdapter.manageSeatQuantity({
        tenantId: tenant.id,
        newSeatCount,
      });
      if (result.error) {
        setError(result.error);
        setSaving(false);
        return;
      }
      onUpdated(newSeatCount);
    } catch (e) {
      console.error('Seat update error:', e);
      setError('Network error. Please try again.');
    }
    setSaving(false);
  };

  const seatDelta = newSeatCount - (tenant.maxSeats || 0);

  return (
    <div className="mb-4 bg-white rounded-2xl border-2 border-purple-200 shadow-sm p-5 animate-[fadeSlideIn_0.2s_ease-out]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-stone-800">Manage Seats</h3>
        <button onClick={onClose} className="text-stone-500 hover:text-stone-600" aria-label="Close seat management">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-stone-50 rounded-lg px-3 py-2 text-center">
          <div className="text-lg font-medium text-stone-800">{activeSeats}</div>
          <div className="text-xs text-stone-500">Active users</div>
        </div>
        <div className="bg-stone-50 rounded-lg px-3 py-2 text-center">
          <div className="text-lg font-medium text-stone-800">{tenant.maxSeats}</div>
          <div className="text-xs text-stone-500">Current seats</div>
        </div>
        <div className="bg-stone-50 rounded-lg px-3 py-2 text-center">
          <div className="text-lg font-medium text-stone-800">{minSeats} to {maxSeats}</div>
          <div className="text-xs text-stone-500">Plan range</div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <label className="text-sm text-stone-600 font-medium">New seat count:</label>
        <input
          type="number"
          min={minSeats}
          max={maxSeats}
          value={newSeatCount}
          onChange={e => setNewSeatCount(parseInt(e.target.value) || minSeats)}
          className="w-24 border border-stone-200 rounded-lg px-3 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
        />
        {seatDelta !== 0 && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${seatDelta > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {seatDelta > 0 ? '+' : ''}{seatDelta} seat{Math.abs(seatDelta) !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {seatDelta !== 0 && (
        <p className="text-xs text-stone-500 mb-3">
          {tenant.stripeSubscriptionId
            ? 'Changes will be prorated on the next billing cycle.'
            : 'Seat count will be updated immediately.'}
        </p>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 rounded-lg px-3 py-2 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-800">
          Cancel
        </button>
        <button
          onClick={handleUpdate}
          disabled={saving || newSeatCount === tenant.maxSeats}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Update seats
        </button>
      </div>
    </div>
  );
}

export { SeatAlerts, SeatManagerButton, SeatManagementPanel };
export default SeatManagementPanel;
